import Button from '@carvana/ds-button';
import React from 'react';
import SomethingWentWrongModal from '|/components/carvana-ds/something-went-wrong-modal/something-went-wrong-modal';
import currency, { removeZeroCents } from '|/helpers/currency';
import { triggerOpenOrderSummary } from '|/hooks/use-open-order-summary';
import styles from './pay-at-order.module.css';
import { PriceIsRightContent } from './price-is-right-content';
import type { CompletePurchasePayAtOrderRenderProps } from '../complete-purchase-shell';
import { Consents } from '../components/pay-at-order/consents';
import { DigitalWallets } from '../components/pay-at-order/digital-wallets';
import { ImpersonationBlockedBanner } from '../components/pay-at-order/impersonation-blocked-banner';
import { PlaidModal } from '../components/pay-at-order/plaid-modal';
import { usePayAtOrder } from '../components/pay-at-order/use-pay-at-order';
import { isPayLaterSelection } from '../components/pay-at-order/utils/digital-wallet';

const getTreatmentButtonText = (
  amountDue: number,
  isPayLater: boolean
): `Pay ${string} now` | 'Place Order' =>
  amountDue > 0 && !isPayLater ? `Pay ${removeZeroCents(currency(amountDue))} now` : 'Place Order';

const PriceIsRightPayAtOrder = ({
  onSubmit,
  editPaymentMethod,
  paymentSummary,
  reviewSummary
}: CompletePurchasePayAtOrderRenderProps) => {
  const { amountDue } = paymentSummary.purchasePaymentBreakdown;
  const isPayLater = isPayLaterSelection(paymentSummary.paymentSelection);
  const treatmentButtonText = getTreatmentButtonText(amountDue, isPayLater);

  const {
    paymentSelection,
    requestForPaymentAmount,
    isSubmitting,
    isButtonDisabled,
    isPlaidModalOpen,
    shouldUseDigitalWallet,
    shouldRenderDigitalWallets,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    setHasCustomerConfirmedSufficientFunds,
    setIsSubmitting,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onDigitalWalletPaymentCollected,
    onPurchaseRolledBack,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  } = usePayAtOrder({
    onSubmit,
    editPaymentMethod,
    buttonText: treatmentButtonText
  });

  return (
    <>
      <div className={styles.container}>
        <PriceIsRightContent
          paymentSummary={paymentSummary}
          reviewSummary={reviewSummary}
          onViewPriceDetails={() => {
            if (reviewSummary) triggerOpenOrderSummary();
          }}
        />

        <div className={styles.actions}>
          {amountDue > 0 && shouldShowConsents && (
            <Consents
              disableClickOnSufficientFundsCheckbox={isSubmitting}
              hasCustomerConfirmedSufficientFunds={hasCustomerConfirmedSufficientFunds}
              setHasCustomerConfirmedSufficientFunds={setHasCustomerConfirmedSufficientFunds}
            />
          )}

          {shouldShowImpersonationText && (
            <ImpersonationBlockedBanner
              shouldUseDigitalWallet={shouldUseDigitalWallet}
              shouldInitiateRequestForPayment={shouldInitiateRequestForPayment}
              selectionType={paymentSelection?.selectionType}
            />
          )}

          {shouldRenderDigitalWallets && (
            <DigitalWallets
              editPaymentMethod={editPaymentMethod}
              setIsSubmitting={setIsSubmitting}
              onSupportChange={setIsDigitalWalletSupported}
              onPaymentCollected={onDigitalWalletPaymentCollected}
              onPurchaseRolledBack={onPurchaseRolledBack}
              onError={handleError}
            />
          )}

          {!shouldUseDigitalWallet && (
            <div className={styles.buttonContainer}>
              <Button
                variant="primary"
                size="md"
                width="100%"
                disabled={isButtonDisabled}
                isLoading={isSubmitting}
                onClick={() => {
                  void submitPayAtOrder();
                }}
                data-analytics-id="pp_complete_purchase_pao_submit"
              >
                {treatmentButtonText}
              </Button>
            </div>
          )}
        </div>
      </div>

      <PlaidModal
        isOpen={isPlaidModalOpen}
        onClose={() => {
          setIsPlaidModalOpen(false);
        }}
        paymentAmount={requestForPaymentAmount ?? 0}
        onSuccess={() => {
          handleSubmit().finally(() => {
            setIsPlaidModalOpen(false);
          });
        }}
        onExit={() => {
          setIsPlaidModalOpen(false);
          onPlaidExit();
        }}
        onError={() => {
          setIsPlaidModalOpen(false);
          handleError();
        }}
      />

      <SomethingWentWrongModal text="collecting your payment" />
    </>
  );
};

export default PriceIsRightPayAtOrder;
